import React, { useState, useEffect } from 'react';
import { Table, Input, Card, Tag, message, Tooltip, Button, Modal, Select, DatePicker, Form, Space, Divider, InputNumber, Checkbox, Popconfirm, Upload, List, Typography, Progress, Image } from 'antd';
import { SearchOutlined, DeleteOutlined, PlusOutlined, LinkOutlined, CopyOutlined, FileTextOutlined, FilePdfOutlined, UploadOutlined, FolderOutlined, InboxOutlined, CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined, HomeOutlined, MailOutlined, LoadingOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';
import authService from '../utils/auth';
import PassportReuseTag from '../components/PassportReuseTag';
import './AdminTourists.css';

const { Option } = Select;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

// 中国团附加证件：本团要求 + 护照国籍 → 该客人要传哪种证件（不需要则返回 null）
// 与后端 utils/chinaDocRule.getChinaDocType 保持一致
const getChinaDocLabel = (record) => {
  if (!record?.chinaDocRequired) return null;
  const n = String(record.nationality || '').toUpperCase();
  if (n.startsWith('CHN') || n.startsWith('中国')) return '身份证';
  if (n.startsWith('TWN') || n.startsWith('台湾')) return '台胞证';
  return null;
};

function AdminTourists() {
  const [tourists, setTourists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  // 服务端分页
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // 筛选相关状态
  const [tours, setTours] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [selectedTourIds, setSelectedTourIds] = useState([]);
  const [selectedDestinationId, setSelectedDestinationId] = useState(null);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);
  const [selectedHotelId, setSelectedHotelId] = useState(null);
  const [selectedTourType, setSelectedTourType] = useState(null);

  // 酒店分配相关状态
  const [hotelModalVisible, setHotelModalVisible] = useState(false);
  const [currentTourist, setCurrentTourist] = useState(null);
  const [touristHotels, setTouristHotels] = useState([]);
  const [availableHotels, setAvailableHotels] = useState([]);
  const [hotelLoading, setHotelLoading] = useState(false);

  // Add-ons分配相关状态
  const [addonModalVisible, setAddonModalVisible] = useState(false);
  const [touristAddons, setTouristAddons] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [addonLoading, setAddonLoading] = useState(false);

  // 文档管理相关状态
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [touristDocuments, setTouristDocuments] = useState([]);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 邮件发送相关状态
  const [sendingEmailId, setSendingEmailId] = useState(null);

  // 批量上传机票相关状态
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [batchUploadDestination, setBatchUploadDestination] = useState(null);
  const [batchUploadDate, setBatchUploadDate] = useState(null);
  const [batchUploadAirline, setBatchUploadAirline] = useState(null);
  const [batchUploadAirlines, setBatchUploadAirlines] = useState([]);
  const [batchUploadFiles, setBatchUploadFiles] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchUploadResult, setBatchUploadResult] = useState(null);
  const [batchUploadProgress, setBatchUploadProgress] = useState(0);
  const [batchUploadCurrentFile, setBatchUploadCurrentFile] = useState('');
  const [batchUploadCompletedCount, setBatchUploadCompletedCount] = useState(0);

  // 批量上传酒店预订单相关状态
  const [batchHotelModalVisible, setBatchHotelModalVisible] = useState(false);
  const [batchHotelDate, setBatchHotelDate] = useState(null);
  const [batchHotelFiles, setBatchHotelFiles] = useState([]);
  const [batchHotelUploading, setBatchHotelUploading] = useState(false);
  const [batchHotelResult, setBatchHotelResult] = useState(null);
  const [batchHotelProgress, setBatchHotelProgress] = useState(0);
  const [batchHotelCurrentFile, setBatchHotelCurrentFile] = useState('');
  const [batchHotelCompletedCount, setBatchHotelCompletedCount] = useState(0);

  // 护照查看相关状态
  const [passportModalVisible, setPassportModalVisible] = useState(false);
  const [passportModalRecord, setPassportModalRecord] = useState(null);
  // 中国团附加证件（身份证/台胞证）查看弹窗 + 代客上传
  const [chinaDocModalVisible, setChinaDocModalVisible] = useState(false);
  const [chinaDocModalRecord, setChinaDocModalRecord] = useState(null);
  const [chinaDocUploadingId, setChinaDocUploadingId] = useState(null);

  // 导出CSV相关状态
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (authService.isAuthenticated()) {
      fetchTours();
      fetchDestinations();
      fetchAvailableHotels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 搜索防抖：停止输入 400ms 后才发请求
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // 筛选条件变化时回到第 1 页
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedTourIds, selectedDestinationId, selectedDepartureDate, selectedHotelId, selectedTourType]);

  // 服务端分页：页码/筛选变化时重新请求
  useEffect(() => {
    if (authService.isAuthenticated()) {
      fetchTourists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, selectedTourIds, selectedDestinationId, selectedDepartureDate, selectedHotelId, selectedTourType]);

  // 获取旅游产品列表
  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API_BASE}/tours`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setTours(response.data.data);
      }
    } catch (error) {
      console.error('获取旅游产品列表失败:', error);
    }
  };

  // 获取目的地列表
  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/destinations`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setDestinations(response.data.data);
      }
    } catch (error) {
      console.error('获取目的地列表失败:', error);
    }
  };

  // 代客上传身份证/台胞证：后端 OCR 提取姓名+号码，识别不出会返回错误提示手工补
  const handleChinaDocUpload = async (record, file) => {
    const label = getChinaDocLabel(record) || '证件';
    setChinaDocUploadingId(record.id);
    try {
      const formData = new FormData();
      formData.append('chinaDoc', file);
      const res = await axios.post(
        `${API_BASE}/tourists/${record.id}/update-china-doc`,
        formData,
        { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        message.success(res.data.message || `${label}上传成功`);
        fetchTourists();
      }
    } catch (err) {
      message.error(err.response?.data?.error || `${label}上传失败`);
    } finally {
      setChinaDocUploadingId(null);
    }
  };

  const fetchTourists = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', pageSize);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (selectedTourIds.length > 0) params.append('tourIds', selectedTourIds.join(','));
      if (selectedDestinationId) params.append('destinationId', selectedDestinationId);
      if (selectedDepartureDate) params.append('departureDate', selectedDepartureDate);
      if (selectedHotelId) params.append('hotelId', selectedHotelId);
      if (selectedTourType) params.append('tourType', selectedTourType);

      const response = await axios.get(`${API_BASE}/tourists?${params.toString()}`, {
        headers: authService.getAuthHeaders()
      });
      setTourists(response.data.data);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error('获取游客列表失败');
      console.error('Fetch tourists error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取可用酒店列表
  const fetchAvailableHotels = async () => {
    try {
      const response = await axios.get(`${API_BASE}/hotels?isActive=true`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setAvailableHotels(response.data.data);
      }
    } catch (error) {
      console.error('获取酒店列表失败:', error);
    }
  };

  // 获取可用Add-ons列表
  const fetchAvailableAddons = async () => {
    try {
      const response = await axios.get(`${API_BASE}/addon-products?isActive=true`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setAvailableAddons(response.data.data);
      }
    } catch (error) {
      console.error('获取Add-ons列表失败:', error);
    }
  };

  // 打开酒店分配弹窗
  const openHotelModal = async (tourist) => {
    setCurrentTourist(tourist);
    setHotelLoading(true);
    setHotelModalVisible(true);

    try {
      await fetchAvailableHotels();
      const response = await axios.get(`${API_BASE}/tourist-assignments/${tourist.id}/hotels`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setTouristHotels(response.data.data || []);
      }
    } catch (error) {
      message.error('获取酒店分配信息失败');
    } finally {
      setHotelLoading(false);
    }
  };

  // 保存酒店分配
  const saveHotelAssignments = async () => {
    if (!currentTourist) return;

    // 验证所有酒店安排的数据完整性
    for (let i = 0; i < touristHotels.length; i++) {
      const h = touristHotels[i];
      if (!h.hotelId) {
        message.error(`第${i + 1}条酒店安排：请选择酒店`);
        return;
      }
      if (!h.checkInDate) {
        message.error(`第${i + 1}条酒店安排：请选择入住日期`);
        return;
      }
      if (!h.checkOutDate) {
        message.error(`第${i + 1}条酒店安排：请选择退房日期`);
        return;
      }
    }

    setHotelLoading(true);
    try {
      const hotels = touristHotels.map((h, index) => ({
        hotelId: h.hotelId,
        checkInDate: h.checkInDate,
        checkOutDate: h.checkOutDate,
        roomType: h.roomType || null,
        sortOrder: index
      }));

      await axios.put(
        `${API_BASE}/tourist-assignments/${currentTourist.id}/hotels`,
        { hotels },
        { headers: authService.getAuthHeaders() }
      );

      message.success('酒店分配保存成功');
      setHotelModalVisible(false);
    } catch (error) {
      message.error('保存酒店分配失败');
    } finally {
      setHotelLoading(false);
    }
  };

  // 添加酒店安排
  const addHotelAssignment = () => {
    setTouristHotels([
      ...touristHotels,
      {
        hotelId: null,
        checkInDate: null,
        checkOutDate: null,
        roomType: ''
      }
    ]);
  };

  // 删除酒店安排
  const removeHotelAssignment = (index) => {
    const newHotels = [...touristHotels];
    newHotels.splice(index, 1);
    setTouristHotels(newHotels);
  };

  // 更新酒店安排字段
  const updateHotelField = (index, field, value) => {
    const newHotels = [...touristHotels];
    newHotels[index] = { ...newHotels[index], [field]: value };
    setTouristHotels(newHotels);
  };

  // 打开Add-ons分配弹窗
  const openAddonModal = async (tourist) => {
    setCurrentTourist(tourist);
    setAddonLoading(true);
    setAddonModalVisible(true);

    try {
      await fetchAvailableAddons();
      const response = await axios.get(`${API_BASE}/tourist-assignments/${tourist.id}/addons`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setTouristAddons(response.data.data || []);
      }
    } catch (error) {
      message.error('获取Add-ons分配信息失败');
    } finally {
      setAddonLoading(false);
    }
  };

  // 保存Add-ons分配
  const saveAddonAssignments = async () => {
    if (!currentTourist) return;

    setAddonLoading(true);
    try {
      const addOns = touristAddons.map(a => ({
        addonProductId: a.addonProductId,
        quantity: a.quantity || 1,
        notes: a.notes || null
      }));

      await axios.put(
        `${API_BASE}/tourist-assignments/${currentTourist.id}/addons`,
        { addOns },
        { headers: authService.getAuthHeaders() }
      );

      message.success('Add-ons分配保存成功');
      setAddonModalVisible(false);
      fetchTourists(); // 刷新列表
    } catch (error) {
      message.error('保存Add-ons分配失败');
    } finally {
      setAddonLoading(false);
    }
  };

  // 生成Add-on通知书链接
  const generateAddOnNoticeLink = async (touristAddOnId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/addon-notices/${touristAddOnId}/generate-link`,
        {},
        { headers: authService.getAuthHeaders() }
      );
      if (response.data.success) {
        const link = response.data.data.addOnNoticeLink;
        // 更新本地状态
        setTouristAddons(prev => prev.map(a =>
          a.id === touristAddOnId ? { ...a, addOnNoticeLink: link } : a
        ));
        message.success('Add-on通知书链接生成成功');
        return link;
      }
    } catch (error) {
      message.error('生成链接失败');
    }
  };

  // 复制文本到剪贴板的兼容方法
  const copyToClipboard = (text, successMsg) => {
    // 优先使用 Clipboard API (需要 HTTPS 或 localhost，即 isSecureContext)
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(successMsg);
      }).catch(() => {
        fallbackCopyToClipboard(text, successMsg);
      });
    } else {
      fallbackCopyToClipboard(text, successMsg);
    }
  };

  // Fallback 复制方法 (兼容非 HTTPS 环境)
  const fallbackCopyToClipboard = (text, successMsg) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      message.success(successMsg);
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
    document.body.removeChild(textArea);
  };

  // 复制Add-on通知书链接
  const copyAddOnNoticeLink = (link) => {
    const fullUrl = `${window.location.origin}/addon-notice/${link}`;
    copyToClipboard(fullUrl, '链接已复制到剪贴板');
  };

  // 生成出团通知书链接
  const generateNoticeLink = async (touristId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/tour-notices/tourist/${touristId}/generate-link`,
        {},
        { headers: authService.getAuthHeaders() }
      );
      if (response.data.success) {
        message.success('出团通知书链接生成成功');
        // 更新本地数据
        setTourists(prev => prev.map(t =>
          t.id === touristId ? { ...t, noticeLink: response.data.data.noticeLink } : t
        ));
        return response.data.data.noticeLink;
      }
    } catch (error) {
      message.error(error.response?.data?.message || '生成链接失败');
    }
  };

  // 复制出团通知书链接
  const copyNoticeLink = (link) => {
    const fullUrl = `${window.location.origin}/notice/${link}`;
    copyToClipboard(fullUrl, '出团通知书链接已复制到剪贴板');
  };

  // 发送出团通知书邮件
  const sendNoticeEmail = async (touristId) => {
    setSendingEmailId(touristId);
    try {
      const response = await axios.post(
        `${API_BASE}/tourists/${touristId}/send-notice-email`,
        {},
        { headers: authService.getAuthHeaders() }
      );
      if (response.data.success) {
        message.success(`邮件发送成功，已发送至 ${response.data.data.sentTo}`);
        // 更新本地数据
        setTourists(prev => prev.map(t =>
          t.id === touristId ? { ...t, noticeSentCount: response.data.data.sentCount } : t
        ));
      }
    } catch (error) {
      message.error(error.response?.data?.error || '邮件发送失败');
    } finally {
      setSendingEmailId(null);
    }
  };

  // 切换Add-on选择
  const toggleAddonSelection = (addonProductId) => {
    const existingIndex = touristAddons.findIndex(a => a.addonProductId === addonProductId);
    if (existingIndex >= 0) {
      // 已选中，取消选择
      const newAddons = [...touristAddons];
      newAddons.splice(existingIndex, 1);
      setTouristAddons(newAddons);
    } else {
      // 未选中，添加
      setTouristAddons([
        ...touristAddons,
        { addonProductId, quantity: 1, notes: '' }
      ]);
    }
  };

  // ========== 文档管理相关函数 ==========

  // 打开文档管理弹窗
  const openDocumentModal = async (tourist) => {
    setCurrentTourist(tourist);
    setDocumentLoading(true);
    setDocumentModalVisible(true);

    try {
      const response = await axios.get(`${API_BASE}/tourist-documents/tourist/${tourist.id}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setTouristDocuments(response.data.data || []);
      }
    } catch (error) {
      message.error('获取文档列表失败');
      console.error('获取文档列表失败:', error);
    } finally {
      setDocumentLoading(false);
    }
  };

  // 上传文档
  const handleDocumentUpload = async (file, documentType) => {
    if (!currentTourist) return false;

    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    formData.append('documentName', file.name);

    setUploadingType(documentType);
    setUploadProgress(0);
    try {
      const response = await axios.post(
        `${API_BASE}/tourist-documents/tourist/${currentTourist.id}`,
        formData,
        {
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );

      if (response.data.success) {
        const newDoc = response.data.data;
        message.success(response.data.message || '文档上传成功');
        // 刷新文档列表
        setTouristDocuments(prev => [...prev, newDoc]);

        // 如果是机票类型且正在处理OCR，开始轮询OCR状态
        if (documentType === 'flight' && newDoc.ocrStatus === 'processing') {
          pollOcrStatus(newDoc.id);
        }
      }
    } catch (error) {
      message.error(error.response?.data?.error || '上传失败');
    } finally {
      setUploadingType(null);
      setUploadProgress(0);
    }

    return false; // 阻止默认上传行为
  };

  // 删除文档
  const handleDeleteDocument = async (documentId) => {
    try {
      await axios.delete(`${API_BASE}/tourist-documents/${documentId}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('文档删除成功');
      setTouristDocuments(prev => prev.filter(d => d.id !== documentId));
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 轮询机票OCR识别状态
  const pollOcrStatus = async (documentId, maxAttempts = 30) => {
    let attempts = 0;
    const poll = async () => {
      if (attempts >= maxAttempts) {
        message.warning('机票识别超时，请稍后刷新查看结果');
        return;
      }
      attempts++;

      try {
        const response = await axios.get(`${API_BASE}/tourist-documents/${documentId}/ocr-status`, {
          headers: authService.getAuthHeaders()
        });

        if (response.data.success) {
          const ocrData = response.data.data;
          if (ocrData.ocrStatus === 'success') {
            // 更新文档列表中的对应文档
            setTouristDocuments(prev => prev.map(doc =>
              doc.id === documentId ? { ...doc, ...ocrData } : doc
            ));
            // 根据护照匹配状态显示不同消息
            if (ocrData.passportMatch === false) {
              message.warning(`机票识别完成，但护照号码不匹配！机票护照: ${ocrData.recognizedPassportNumber || '未识别'}`);
            } else if (ocrData.passportMatch === true) {
              message.success(`机票识别成功！票号: ${ocrData.ticketNumber || '未识别'}，护照匹配`);
            } else {
              message.success(`机票识别成功！票号: ${ocrData.ticketNumber || '未识别'}`);
            }
            return;
          } else if (ocrData.ocrStatus === 'failed') {
            setTouristDocuments(prev => prev.map(doc =>
              doc.id === documentId ? { ...doc, ...ocrData } : doc
            ));
            message.error(`机票识别失败: ${ocrData.ocrError || '未知错误'}`);
            return;
          }
          // 如果还在处理中，继续轮询
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('轮询OCR状态失败:', error);
        setTimeout(poll, 2000);
      }
    };

    setTimeout(poll, 2000); // 2秒后开始第一次轮询
  };

  // 文档类型标签配置
  const documentTypeConfig = {
    hotel: { label: '酒店预订单', color: 'blue' },
    flight: { label: '机票', color: 'green' },
    addon: { label: 'Add-ons产品', color: 'purple' }
  };

  // ========== 批量上传机票相关函数 ==========

  // 打开批量上传弹窗
  const openBatchUploadModal = async () => {
    setBatchUploadDestination(null);
    setBatchUploadDate(null);
    setBatchUploadAirline(null);
    setBatchUploadFiles([]);
    setBatchUploadResult(null);
    setBatchUploadModalVisible(true);
    // 获取订票系统列表
    try {
      const res = await axios.get(`${API_BASE}/airlines?isActive=true`, {
        headers: authService.getAuthHeaders()
      });
      if (res.data.success) {
        setBatchUploadAirlines(res.data.data);
      }
    } catch (err) {
      console.error('获取订票系统列表失败:', err);
    }
  };

  // 处理批量上传
  const handleBatchUpload = async () => {
    if (!batchUploadDate) {
      message.error('请选择出发日期');
      return;
    }
    if (!batchUploadAirline) {
      message.error('请选择订票系统');
      return;
    }
    if (batchUploadFiles.length === 0) {
      message.error('请选择要上传的机票文件');
      return;
    }

    setBatchUploading(true);
    setBatchUploadResult(null);
    setBatchUploadProgress(0);
    setBatchUploadCurrentFile('');
    setBatchUploadCompletedCount(0);

    const totalFiles = batchUploadFiles.length;
    const results = {
      success: [],
      notMatched: [],
      errors: []
    };

    try {
      // 逐个文件上传
      for (let i = 0; i < totalFiles; i++) {
        const file = batchUploadFiles[i];
        setBatchUploadCurrentFile(file.name);
        setBatchUploadProgress(Math.round((i / totalFiles) * 100));

        const formData = new FormData();
        formData.append('departureDate', batchUploadDate);
        formData.append('airlineId', batchUploadAirline);
        formData.append('files', file);

        try {
          const response = await axios.post(
            `${API_BASE}/tourist-documents/batch-upload-flights`,
            formData,
            {
              headers: {
                ...authService.getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            const data = response.data.data;
            if (data.success) results.success.push(...data.success);
            if (data.notMatched) results.notMatched.push(...data.notMatched);
            if (data.errors) results.errors.push(...data.errors);
          }
        } catch (err) {
          results.errors.push({
            fileName: file.name,
            error: err.response?.data?.error || '上传失败'
          });
        }

        setBatchUploadCompletedCount(i + 1);
      }

      setBatchUploadProgress(100);
      setBatchUploadResult(results);

      const successCount = results.success.length;
      const notMatchedCount = results.notMatched.length;
      const errorCount = results.errors.length;
      message.success(`上传完成: ${successCount} 成功, ${notMatchedCount} 未匹配, ${errorCount} 失败`);

      fetchTourists();
    } catch (error) {
      message.error(error.response?.data?.error || '批量上传失败');
    } finally {
      setBatchUploading(false);
      setBatchUploadProgress(0);
      setBatchUploadCurrentFile('');
      setBatchUploadCompletedCount(0);
    }
  };

  // 处理文件选择/拖拽
  const handleBatchFileChange = (info) => {
    // 过滤出有效的PDF文件
    const fileList = info.fileList
      .filter(file => file.originFileObj && file.type === 'application/pdf')
      .map(file => file.originFileObj);
    setBatchUploadFiles(fileList);
  };

  // 重置批量上传状态
  const resetBatchUpload = () => {
    setBatchUploadFiles([]);
    setBatchUploadResult(null);
    setBatchUploadAirline(null);
  };

  // ========== 批量上传酒店预订单相关函数 ==========

  // 打开批量上传酒店预订单弹窗
  const openBatchHotelModal = () => {
    setBatchHotelDate(null);
    setBatchHotelFiles([]);
    setBatchHotelResult(null);
    setBatchHotelModalVisible(true);
  };

  // 处理批量上传酒店预订单
  const handleBatchHotelUpload = async () => {
    if (!batchHotelDate) {
      message.error('请选择出发日期');
      return;
    }
    if (batchHotelFiles.length === 0) {
      message.error('请选择要上传的酒店预订单文件');
      return;
    }

    setBatchHotelUploading(true);
    setBatchHotelResult(null);
    setBatchHotelProgress(0);
    setBatchHotelCurrentFile('');
    setBatchHotelCompletedCount(0);

    const totalFiles = batchHotelFiles.length;
    const results = {
      success: [],
      notMatched: [],
      errors: []
    };

    try {
      // 逐个文件上传
      for (let i = 0; i < totalFiles; i++) {
        const file = batchHotelFiles[i];
        setBatchHotelCurrentFile(file.name);
        setBatchHotelProgress(Math.round((i / totalFiles) * 100));

        const formData = new FormData();
        formData.append('departureDate', batchHotelDate);
        formData.append('files', file);

        try {
          const response = await axios.post(
            `${API_BASE}/tourist-documents/batch-upload-hotels`,
            formData,
            {
              headers: {
                ...authService.getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            const data = response.data.data;
            // 合并结果
            if (data.success) results.success.push(...data.success);
            if (data.notMatched) results.notMatched.push(...data.notMatched);
            if (data.errors) results.errors.push(...data.errors);
          }
        } catch (err) {
          results.errors.push({
            fileName: file.name,
            error: err.response?.data?.error || '上传失败'
          });
        }

        setBatchHotelCompletedCount(i + 1);
      }

      // 设置最终结果
      setBatchHotelProgress(100);
      setBatchHotelResult(results);

      const successCount = results.success.length;
      const notMatchedCount = results.notMatched.length;
      const errorCount = results.errors.length;
      message.success(`上传完成: ${successCount} 成功, ${notMatchedCount} 未匹配, ${errorCount} 失败`);

      // 刷新游客列表
      fetchTourists();
    } catch (error) {
      message.error(error.response?.data?.error || '批量上传失败');
    } finally {
      setBatchHotelUploading(false);
      setBatchHotelProgress(0);
      setBatchHotelCurrentFile('');
      setBatchHotelCompletedCount(0);
    }
  };

  // 处理酒店预订单文件选择
  const handleBatchHotelFileChange = (info) => {
    const fileList = info.fileList
      .filter(file => file.originFileObj && file.type === 'application/pdf')
      .map(file => file.originFileObj);
    setBatchHotelFiles(fileList);
  };

  // 重置批量上传酒店预订单状态
  const resetBatchHotelUpload = () => {
    setBatchHotelFiles([]);
    setBatchHotelResult(null);
  };

  // 导出CSV功能
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // 构建查询参数，与当前筛选条件一致
      const params = new URLSearchParams();
      if (selectedTourIds.length > 0) {
        params.append('tourIds', selectedTourIds.join(','));
      }
      if (selectedDestinationId) {
        params.append('destinationId', selectedDestinationId);
      }
      if (selectedDepartureDate) {
        params.append('departureDate', selectedDepartureDate);
      }
      if (selectedHotelId) {
        params.append('hotelId', selectedHotelId);
      }
      if (selectedTourType) {
        params.append('tourType', selectedTourType);
      }
      if (searchText) {
        params.append('search', searchText);
      }

      const response = await axios.get(`${API_BASE}/tourists/export/csv?${params.toString()}`, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob'
      });

      // 创建下载链接
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `游客数据_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(`导出成功，共 ${total} 条记录`);
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    } finally {
      setExporting(false);
    }
  };

  // 根据 tourId 获取 tour 信息（用于筛选目的地和出团日期）
  const getTourInfo = (tourId) => {
    return tours.find(t => t.id === tourId);
  };

  // 辅助函数：获取游客的团类型
  const getTouristTourType = (tourist) => {
    return tourist.tour?.tourType || tourist.tourType || null;
  };

  // 筛选/排序已下推到服务端（分页接口），前端直接使用返回数据
  const filteredTourists = tourists;

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { text: '待上传', color: 'default' },
      uploaded: { text: '已上传', color: 'processing' },
      verified: { text: '已验证', color: 'success' },
      rejected: { text: '已拒绝', color: 'error' }
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 证件类型显示映射
  const documentTypeLabels = {
    passport: '护照',
    nz_driver_license: 'NZ驾照',
    nz_senior_card: 'NZ老年卡',
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'passportName',
      key: 'passportName',
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (name, record) => {
        const displayName = name || record.touristName || '-';
        const isDaytour = getTouristTourType(record) === 'daytour';
        // 仅对非一日游游客检查护照有效期
        let expiryWarning = false;
        if (!isDaytour && record.passportExpiryDate && record.departureDate) {
          const expiry = moment(record.passportExpiryDate);
          const departure = moment(record.departureDate);
          if (expiry.isValid() && departure.isValid()) {
            const monthsDiff = expiry.diff(departure, 'months', true);
            if (monthsDiff < 6) {
              expiryWarning = true;
            }
          }
        }
        return (
          <Tooltip placement="topLeft" title={displayName}>
            <span>
              {displayName}
              {isDaytour && (
                <Tag color="cyan" style={{ marginLeft: 6, fontSize: 11 }}>一日游</Tag>
              )}
              {isDaytour && record.discountPrice && (
                record.discountEligible === false
                  ? <Tooltip title="年龄不满55岁，不符合优惠价条件"><Tag color="red" style={{ marginLeft: 4, fontSize: 11 }}>优惠!</Tag></Tooltip>
                  : <Tag color="gold" style={{ marginLeft: 4, fontSize: 11 }}>优惠</Tag>
              )}
              {expiryWarning && (
                <Tooltip title={`护照有效期不足6个月（${moment(record.passportExpiryDate).format('YYYY-MM-DD')}）`}>
                  <WarningOutlined style={{ color: '#fa8c16', marginLeft: 6, fontSize: 14 }} />
                </Tooltip>
              )}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '护照号码 / 证件号码',
      key: 'identityNumber',
      width: 160,
      render: (_, record) => {
        const isDaytour = getTouristTourType(record) === 'daytour';
        if (isDaytour) {
          const docTypeLabel = documentTypeLabels[record.documentType] || record.documentType || '证件';
          const docNumber = record.documentNumber || '-';
          return (
            <span>
              <Tag color="blue" style={{ marginRight: 4, fontSize: 11 }}>{docTypeLabel}</Tag>
              {docNumber}
            </span>
          );
        }
        return <span>{record.passportNumber || '-'}</span>;
      },
    },
    {
      title: '护照',
      key: 'passportView',
      width: 90,
      render: (_, record) => {
        const isDaytour = getTouristTourType(record) === 'daytour';
        if (isDaytour) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const reuseTag = <PassportReuseTag reuse={record.passportReuse} />;
        if (record.passportPhoto) {
          return (
            <Space direction="vertical" size={2}>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                style={{ padding: 0 }}
                onClick={() => {
                  setPassportModalRecord(record);
                  setPassportModalVisible(true);
                }}
              >
                查看
              </Button>
              {reuseTag}
            </Space>
          );
        }
        return record.passportReuse ? reuseTag : <span style={{ color: '#999' }}>-</span>;
      },
    },
    {
      // 中国团附加证件：中国护照要身份证、台湾护照要台胞证（机加酒产品不要求，见产品管理开关）
      title: '身份证/台胞证',
      key: 'chinaDoc',
      width: 120,
      render: (_, record) => {
        const label = getChinaDocLabel(record);
        if (!label) return <span style={{ color: '#999' }}>-</span>;
        if (record.chinaDocPhoto) {
          return (
            <Space size={4}>
              <Tag color="green" style={{ fontSize: 11, marginRight: 0 }}>{label}</Tag>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setChinaDocModalRecord(record);
                  setChinaDocModalVisible(true);
                }}
              >
                查看
              </Button>
            </Space>
          );
        }
        return (
          <Space size={4}>
            <Tag color="red" style={{ fontSize: 11, marginRight: 0 }}>缺{label}</Tag>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => { handleChinaDocUpload(record, file); return false; }}
            >
              <Button
                type="link"
                size="small"
                icon={<UploadOutlined />}
                loading={chinaDocUploadingId === record.id}
              >
                上传
              </Button>
            </Upload>
          </Space>
        );
      },
    },
    {
      title: '国籍',
      dataIndex: 'nationality',
      key: 'nationality',
      width: 60,
      render: (nationality) => nationality || '-',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
      render: (gender) => {
        if (gender === 'M' || gender === 'Male') return '男';
        if (gender === 'F' || gender === 'Female') return '女';
        return gender || '-';
      },
    },
    {
      title: '出生日期',
      dataIndex: 'passportBirthDate',
      key: 'passportBirthDate',
      width: 120,
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: '旅游产品',
      dataIndex: 'tourName',
      key: 'tourName',
      width: 150,
      ellipsis: true,
    },
    {
      title: '出团日期',
      dataIndex: 'departureDate',
      key: 'departureDate',
      width: 120,
      sorter: (a, b) => {
        // 处理空值：没有日期的排到最后
        if (!a.departureDate && !b.departureDate) return 0;
        if (!a.departureDate) return 1;
        if (!b.departureDate) return -1;

        // 正常日期排序
        return moment(a.departureDate).valueOf() - moment(b.departureDate).valueOf();
      },
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'ascend', // 默认按出团日期升序
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: 'Add-Ons产品',
      dataIndex: 'addOnsNames',
      key: 'addOns',
      width: 200,
      ellipsis: false,
      render: (addOnsNames, record) => {
        // 一日游不显示 Add-Ons
        if (getTouristTourType(record) === 'daytour') {
          return <span style={{ color: '#999' }}>-</span>;
        }

        // 优先使用新的addOnsNames字段
        if (addOnsNames && addOnsNames.length > 0) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {addOnsNames.map((name, index) => (
                <Tag key={index} color="purple" style={{ margin: 0 }}>
                  {name}
                </Tag>
              ))}
            </div>
          );
        }

        // 兼容旧的dayTrips数据
        const dayTrips = record.dayTrips;
        if (!dayTrips) return <span style={{ color: '#999' }}>无</span>;

        const addOnsList = [
          { key: 'city_tour', label: 'City Tour', color: 'blue' },
          { key: 'tango_show', label: 'Tango Show', color: 'purple' },
          { key: 'tango_dinner', label: 'Tango+Dinner', color: 'magenta' },
          { key: 'iguazu_falls', label: 'Iguazu-AR', color: 'green' },
          { key: 'iguazu_ar_br', label: 'Iguazu-AR+BR', color: 'cyan' }
        ];

        const purchased = addOnsList.filter(addOn => dayTrips[addOn.key] === true);

        if (purchased.length === 0) {
          return <span style={{ color: '#999' }}>无</span>;
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {purchased.map(addOn => (
              <Tag key={addOn.key} color={addOn.color} style={{ margin: 0 }}>
                {addOn.label}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: '销售',
      dataIndex: 'salesName',
      key: 'salesName',
      width: 100,
      render: (name) => (
        <Tag color="blue">{name}</Tag>
      ),
    },
    {
      title: '酒店',
      key: 'hotel',
      width: 150,
      fixed: 'right',
      render: (_, record) => {
        // 一日游不显示酒店分配
        if (getTouristTourType(record) === 'daytour') {
          return <span style={{ color: '#999', fontSize: 12 }}>不适用</span>;
        }
        if (record.firstHotelName) {
          return (
            <Tooltip title={record.hotelCount > 1 ? `共${record.hotelCount}家酒店，点击查看/编辑` : '点击查看/编辑'}>
              <Button
                type="link"
                size="small"
                onClick={() => openHotelModal(record)}
                style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}
              >
                <span style={{ color: '#52c41a' }}>
                  {record.firstHotelName}
                  {record.hotelCount > 1 && <span style={{ color: '#999' }}> +{record.hotelCount - 1}</span>}
                </span>
              </Button>
            </Tooltip>
          );
        }
        return (
          <span style={{ color: '#999', fontSize: 12 }}>未分配</span>
        );
      },
    },
    {
      title: '文档',
      key: 'documents',
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        const hasDocuments = record.documentCount > 0;
        return (
          <Tooltip title={hasDocuments ? `已上传 ${record.documentCount} 个文档` : '未上传文档'}>
            <Button
              type="link"
              size="small"
              icon={<FolderOutlined />}
              onClick={() => openDocumentModal(record)}
              style={{ color: hasDocuments ? '#52c41a' : '#999' }}
            >
              {hasDocuments ? '查看' : '未上传'}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: '出团通知书',
      dataIndex: 'noticeLink',
      key: 'noticeLink',
      width: 100,
      fixed: 'right',
      render: (noticeLink, record) => {
        if (!record.noticePublished) {
          return <Tag color="default">未发布</Tag>;
        }
        if (noticeLink) {
          return (
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => window.open(`/notice/${noticeLink}`, '_blank')}
            >
              查看
            </Button>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => generateNoticeLink(record.id)}
          >
            生成
          </Button>
        );
      },
    },
    {
      title: '发送邮件',
      key: 'sendEmail',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const isSending = sendingEmailId === record.id;
        const sentCount = record.noticeSentCount || 0;

        // 未发布或没有链接时不能发送
        if (!record.noticePublished || !record.noticeLink) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        if (sentCount > 0) {
          return (
            <Tooltip title={`已发送 ${sentCount} 次，点击再次发送`}>
              <Button
                type="link"
                size="small"
                icon={isSending ? <LoadingOutlined /> : <MailOutlined />}
                onClick={() => sendNoticeEmail(record.id)}
                disabled={isSending}
                style={{ color: '#52c41a' }}
              >
                已发送({sentCount})
              </Button>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="发送出团通知书到销售邮箱">
            <Button
              type="link"
              size="small"
              icon={isSending ? <LoadingOutlined /> : <MailOutlined />}
              onClick={() => sendNoticeEmail(record.id)}
              disabled={isSending}
            >
              发送
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="admin-tourists">
      <Card
        title="游客管理"
        className="tourists-card"
        extra={
          <div className="card-extra">
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={openBatchUploadModal}
              style={{ marginRight: 8 }}
            >
              批量上传机票
            </Button>
            <Button
              icon={<HomeOutlined />}
              onClick={openBatchHotelModal}
              style={{ marginRight: 8 }}
            >
              批量上传酒店预订单
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              loading={exporting}
              style={{ marginRight: 16 }}
            >
              导出CSV
            </Button>
            <span style={{ color: '#666' }}>
              总计：{total} 人
            </span>
          </div>
        }
      >
        <div className="search-bar" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <Input
            placeholder="搜索护照姓名、护照号、销售或旅游产品"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 350 }}
            allowClear
          />
          <Select
            placeholder="筛选目的地"
            style={{ width: 150 }}
            value={selectedDestinationId}
            onChange={(destId) => {
              setSelectedDestinationId(destId);
              // 当目的地改变时，检查当前选择的出团日期是否属于新目的地，如果不属于则清除
              if (destId && selectedDepartureDate) {
                const availableDates = tours
                  .filter(t => t.destinationId === destId)
                  .map(t => t.departureDate ? moment(t.departureDate).format('YYYY-MM-DD') : null)
                  .filter(Boolean);
                if (!availableDates.includes(selectedDepartureDate)) {
                  setSelectedDepartureDate(null);
                }
              }
            }}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {destinations.map(dest => (
              <Option key={dest.id} value={dest.id}>{dest.name}</Option>
            ))}
          </Select>
          <Select
            placeholder="筛选出团日期"
            style={{ width: 150 }}
            value={selectedDepartureDate}
            onChange={setSelectedDepartureDate}
            allowClear
            showSearch
          >
            {/* 从tours中提取不重复的出团日期，如果选择了目的地则只显示该目的地的日期 */}
            {[...new Set(
              tours
                .filter(t => !selectedDestinationId || t.destinationId === selectedDestinationId)
                .map(t => t.departureDate ? moment(t.departureDate).format('YYYY-MM-DD') : null)
                .filter(Boolean)
            )]
              .sort()
              .map(date => (
                <Option key={date} value={date}>{date}</Option>
              ))}
          </Select>
          <Select
            placeholder="筛选酒店"
            style={{ width: 200 }}
            value={selectedHotelId}
            onChange={setSelectedHotelId}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {availableHotels.map(hotel => (
              <Option key={hotel.id} value={hotel.id}>{hotel.name}</Option>
            ))}
          </Select>
          <Select
            placeholder="团类型"
            style={{ width: 120 }}
            value={selectedTourType}
            onChange={setSelectedTourType}
            allowClear
          >
            <Option value="standard">出境游</Option>
            <Option value="daytour">一日游</Option>
          </Select>
          <Select
            mode="multiple"
            placeholder="筛选旅游产品（可多选）"
            style={{ minWidth: 250, maxWidth: 400 }}
            value={selectedTourIds}
            onChange={setSelectedTourIds}
            allowClear
            showSearch
            optionFilterProp="children"
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}个产品`}
          >
            {tours.map(tour => (
              <Option key={tour.id} value={tour.id}>
                {tour.productName} {tour.departureDate ? `(${moment(tour.departureDate).format('YYYY-MM-DD')})` : ''}
              </Option>
            ))}
          </Select>
          {(selectedTourIds.length > 0 || selectedDestinationId || selectedDepartureDate || selectedHotelId || selectedTourType) && (
            <Button
              type="link"
              onClick={() => {
                setSelectedTourIds([]);
                setSelectedDestinationId(null);
                setSelectedDepartureDate(null);
                setSelectedHotelId(null);
                setSelectedTourType(null);
              }}
            >
              清除筛选
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={filteredTourists}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1750 }}
          size="small"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${t} 人`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      {/* 酒店分配弹窗 */}
      <Modal
        title={`分配酒店 - ${currentTourist?.touristName || currentTourist?.passportName || ''}`}
        open={hotelModalVisible}
        onCancel={() => setHotelModalVisible(false)}
        onOk={saveHotelAssignments}
        okText="保存"
        cancelText="取消"
        width={800}
        confirmLoading={hotelLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <Button type="dashed" onClick={addHotelAssignment} icon={<PlusOutlined />}>
            添加酒店安排
          </Button>
        </div>

        {touristHotels.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
            暂无酒店安排，点击上方按钮添加
          </div>
        ) : (
          touristHotels.map((hotel, index) => (
            <Card
              key={index}
              size="small"
              style={{ marginBottom: 12 }}
              title={`酒店 ${index + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeHotelAssignment(index)}
                >
                  删除
                </Button>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 2 }}>
                    <div style={{ marginBottom: 4, color: '#666' }}>选择酒店</div>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="选择酒店"
                      value={hotel.hotelId}
                      onChange={(value) => updateHotelField(index, 'hotelId', value)}
                      showSearch
                      optionFilterProp="children"
                    >
                      {availableHotels.map(h => (
                        <Option key={h.id} value={h.id}>
                          {h.name} {h.starLevel ? `(${h.starLevel}星)` : ''} - {h.destination?.name || '未知目的地'}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 4, color: '#666' }}>房型</div>
                    <Input
                      placeholder="房型（可选）"
                      value={hotel.roomType}
                      onChange={(e) => updateHotelField(index, 'roomType', e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 4, color: '#666' }}>入住日期</div>
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="入住日期"
                      value={hotel.checkInDate ? dayjs(hotel.checkInDate) : null}
                      onChange={(date) => updateHotelField(index, 'checkInDate', date ? date.format('YYYY-MM-DD') : null)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 4, color: '#666' }}>退房日期</div>
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="退房日期"
                      value={hotel.checkOutDate ? dayjs(hotel.checkOutDate) : null}
                      onChange={(date) => updateHotelField(index, 'checkOutDate', date ? date.format('YYYY-MM-DD') : null)}
                    />
                  </div>
                </div>
              </Space>
            </Card>
          ))
        )}
      </Modal>

      {/* Add-ons分配弹窗 */}
      <Modal
        title={`分配Add-ons - ${currentTourist?.touristName || currentTourist?.passportName || ''}`}
        open={addonModalVisible}
        onCancel={() => setAddonModalVisible(false)}
        onOk={saveAddonAssignments}
        okText="保存"
        cancelText="取消"
        width={700}
        confirmLoading={addonLoading}
      >
        {availableAddons.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
            暂无可用的Add-ons产品
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, color: '#666' }}>
              选择该游客购买的Add-ons产品：
            </div>
            {availableAddons.map(addon => {
              const selectedAddon = touristAddons.find(a => a.addonProductId === addon.id);
              const isSelected = !!selectedAddon;
              return (
                <Card
                  key={addon.id}
                  size="small"
                  style={{
                    marginBottom: 8,
                    border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    background: isSelected ? '#e6f7ff' : '#fff'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div
                      style={{ flex: 1, cursor: 'pointer' }}
                      onClick={() => toggleAddonSelection(addon.id)}
                    >
                      <Checkbox checked={isSelected} style={{ marginRight: 8 }} />
                      <span style={{ fontWeight: 500 }}>{addon.name}</span>
                      {addon.dayNumber && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>Day {addon.dayNumber}</Tag>
                      )}
                      <span style={{ color: '#666', marginLeft: 8 }}>
                        {addon.destination?.name || '通用'}
                      </span>
                    </div>
                    {/* 已保存的Add-on显示通知书链接操作 */}
                    {isSelected && selectedAddon.id && (
                      <Space size="small" onClick={(e) => e.stopPropagation()}>
                        {selectedAddon.addOnNoticeLink ? (
                          <Tooltip title="复制通知书链接">
                            <Button
                              type="primary"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => copyAddOnNoticeLink(selectedAddon.addOnNoticeLink)}
                            >
                              复制链接
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="生成单独的Add-on通知书链接">
                            <Button
                              type="default"
                              size="small"
                              icon={<LinkOutlined />}
                              onClick={() => generateAddOnNoticeLink(selectedAddon.id)}
                            >
                              生成通知书
                            </Button>
                          </Tooltip>
                        )}
                      </Space>
                    )}
                  </div>
                  {isSelected && selectedAddon.addOnNoticeLink && (
                    <div style={{ marginTop: 8, paddingLeft: 24 }}>
                      <Tag color="green" style={{ cursor: 'pointer' }} onClick={() => window.open(`/addon-notice/${selectedAddon.addOnNoticeLink}`, '_blank')}>
                        <LinkOutlined /> 查看通知书
                      </Tag>
                    </div>
                  )}
                </Card>
              );
            })}
            <Divider style={{ margin: '16px 0' }} />
            <div style={{ color: '#999', fontSize: 12 }}>
              提示：先保存Add-ons分配，然后可以为每个Add-on生成独立的通知书链接
            </div>
          </div>
        )}
      </Modal>

      {/* 文档管理弹窗 */}
      <Modal
        title={`文档管理 - ${currentTourist?.touristName || currentTourist?.passportName || ''}`}
        open={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setDocumentModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            disabled={
              uploadingType !== null ||
              touristDocuments.some(doc => doc.documentType === 'flight' && doc.ocrStatus === 'processing')
            }
            onClick={() => {
              message.success('文档已保存');
              setDocumentModalVisible(false);
            }}
          >
            {uploadingType !== null
              ? '上传中...'
              : touristDocuments.some(doc => doc.documentType === 'flight' && doc.ocrStatus === 'processing')
                ? '识别中...'
                : '提交'}
          </Button>
        ]}
      >
        <div style={{ marginBottom: 24 }}>
          <Typography.Text type="secondary">
            上传游客的酒店预订单、机票、Add-ons产品等PDF文档，这些文档将显示在出团通知书页面中
          </Typography.Text>
        </div>

        {/* 上传区域 - 三种文档类型拖拽上传（上下排列） */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {['hotel', 'flight', 'addon'].map(docType => (
            <Upload.Dragger
              key={docType}
              accept={docType === 'addon' ? '.pdf,.jpg,.jpeg,.png' : '.pdf'}
              showUploadList={false}
              beforeUpload={(file) => handleDocumentUpload(file, docType)}
              disabled={uploadingType === docType}
              style={{
                padding: '16px 20px',
                background: uploadingType === docType ? '#f5f5f5' : '#fafafa',
                borderColor: documentTypeConfig[docType].color
              }}
            >
              {uploadingType === docType ? (
                <div style={{ padding: '8px 0' }}>
                  <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 500 }}>
                    正在上传 {documentTypeConfig[docType].label}...
                  </p>
                  <Progress
                    percent={uploadProgress}
                    strokeColor={documentTypeConfig[docType].color}
                    status="active"
                    size="small"
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div>
                    <InboxOutlined style={{ fontSize: 32, color: documentTypeConfig[docType].color }} />
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>
                      {documentTypeConfig[docType].label}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#999' }}>
                      {docType === 'addon' ? '点击或拖拽 PDF/JPG/PNG 文件到此区域上传' : '点击或拖拽 PDF 文件到此区域上传'}
                    </p>
                  </div>
                </div>
              )}
            </Upload.Dragger>
          ))}
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* 已上传文档列表 */}
        {documentLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>加载中...</div>
        ) : touristDocuments.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
            暂无上传的文档
          </div>
        ) : (
          <List
            size="small"
            dataSource={touristDocuments}
            renderItem={(doc) => (
              <List.Item
                actions={[
                  <Tooltip title="查看文档" key="view">
                    <Button
                      type="link"
                      size="small"
                      icon={<FilePdfOutlined />}
                      onClick={() => window.open(doc.fileUrl, '_blank')}
                    >
                      查看
                    </Button>
                  </Tooltip>,
                  <Popconfirm
                    key="delete"
                    title="确定删除此文档?"
                    onConfirm={() => handleDeleteDocument(doc.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  avatar={<FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />}
                  title={
                    <Space wrap>
                      <span>{doc.documentName}</span>
                      <Tag color={documentTypeConfig[doc.documentType]?.color}>
                        {documentTypeConfig[doc.documentType]?.label}
                      </Tag>
                      {doc.documentType === 'flight' && doc.ocrStatus === 'processing' && (
                        <Tag color="orange">识别中...</Tag>
                      )}
                      {doc.documentType === 'flight' && doc.ocrStatus === 'success' && doc.ticketNumber && (
                        <Tag color="green">票号: {doc.ticketNumber}</Tag>
                      )}
                      {doc.documentType === 'flight' && doc.ocrStatus === 'success' && doc.passportMatch === true && (
                        <Tooltip title="机票上的护照号码与游客护照匹配">
                          <Tag color="success" icon={<CheckCircleOutlined />}>护照匹配</Tag>
                        </Tooltip>
                      )}
                      {doc.documentType === 'flight' && doc.ocrStatus === 'success' && doc.passportMatch === false && (
                        <Tooltip title={`护照号码不匹配！机票显示: ${doc.recognizedPassportNumber || '未识别'}，请核实是否上传正确`}>
                          <Tag color="error" icon={<ExclamationCircleOutlined />}>护照不匹配</Tag>
                        </Tooltip>
                      )}
                      {doc.documentType === 'flight' && doc.ocrStatus === 'failed' && (
                        <Tooltip title={doc.ocrError || '识别失败'}>
                          <Tag color="red">识别失败</Tag>
                        </Tooltip>
                      )}
                    </Space>
                  }
                  description={
                    <div style={{ fontSize: 12, color: '#999' }}>
                      <div>
                        {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : ''} ·
                        上传于 {moment(doc.createdAt).format('YYYY-MM-DD HH:mm')}
                      </div>
                      {doc.documentType === 'flight' && doc.ocrStatus === 'success' && doc.recognizedPassengerName && (
                        <div style={{ marginTop: 4 }}>
                          乘客: {doc.recognizedPassengerName}
                          {doc.recognizedPassportNumber && ` | 护照: ${doc.recognizedPassportNumber}`}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* 批量上传机票弹窗 */}
      <Modal
        title="批量上传机票"
        open={batchUploadModalVisible}
        onCancel={() => {
          setBatchUploadModalVisible(false);
          resetBatchUpload();
        }}
        width={700}
        footer={
          batchUploadResult ? [
            <Button key="close" onClick={() => {
              setBatchUploadModalVisible(false);
              resetBatchUpload();
            }}>
              关闭
            </Button>,
            <Button key="upload-more" type="primary" onClick={resetBatchUpload}>
              继续上传
            </Button>
          ] : [
            <Button key="cancel" onClick={() => {
              setBatchUploadModalVisible(false);
              resetBatchUpload();
            }}>
              取消
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={batchUploading}
              onClick={handleBatchUpload}
              disabled={!batchUploadDate || !batchUploadAirline || batchUploadFiles.length === 0}
            >
              开始上传
            </Button>
          ]
        }
      >
        {batchUploadResult ? (
          // 上传结果展示
          <div>
            <div style={{ marginBottom: 16 }}>
              <Space size="large">
                <Tag color="success" style={{ padding: '4px 12px', fontSize: 14 }}>
                  <CheckCircleOutlined /> 成功: {batchUploadResult.success?.length || 0}
                </Tag>
                <Tag color="warning" style={{ padding: '4px 12px', fontSize: 14 }}>
                  <WarningOutlined /> 未匹配: {batchUploadResult.notMatched?.length || 0}
                </Tag>
                <Tag color="error" style={{ padding: '4px 12px', fontSize: 14 }}>
                  <ExclamationCircleOutlined /> 失败: {batchUploadResult.errors?.length || 0}
                </Tag>
              </Space>
            </div>

            {batchUploadResult.success?.length > 0 && (
              <Card size="small" title="上传成功" style={{ marginBottom: 12 }}>
                <List
                  size="small"
                  dataSource={batchUploadResult.success}
                  renderItem={(item) => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      <span>{item.fileName}</span>
                      <span style={{ marginLeft: 8, color: '#666' }}>→ {item.touristName}</span>
                      {item.extractedPassport && (
                        <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                          (护照: {item.extractedPassport})
                        </span>
                      )}
                    </List.Item>
                  )}
                  style={{ maxHeight: 200, overflow: 'auto' }}
                />
              </Card>
            )}

            {batchUploadResult.notMatched?.length > 0 && (
              <Card size="small" title="未匹配游客" style={{ marginBottom: 12 }}>
                <List
                  size="small"
                  dataSource={batchUploadResult.notMatched}
                  renderItem={(item) => (
                    <List.Item>
                      <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                      <span>{item.fileName}</span>
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        {item.extractedName && `(识别: ${item.extractedName})`}
                        {item.extractedPassport && ` 护照: ${item.extractedPassport}`}
                        {' '}{item.reason}
                      </span>
                    </List.Item>
                  )}
                  style={{ maxHeight: 150, overflow: 'auto' }}
                />
              </Card>
            )}

            {batchUploadResult.errors?.length > 0 && (
              <Card size="small" title="上传失败" style={{ marginBottom: 12 }}>
                <List
                  size="small"
                  dataSource={batchUploadResult.errors}
                  renderItem={(item) => (
                    <List.Item>
                      <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      <span>{item.fileName}</span>
                      <span style={{ marginLeft: 8, color: '#ff4d4f' }}>{item.error}</span>
                    </List.Item>
                  )}
                  style={{ maxHeight: 100, overflow: 'auto' }}
                />
              </Card>
            )}
          </div>
        ) : batchUploading ? (
          // 上传中显示进度条
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ marginBottom: 16 }}>
              <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </div>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
              正在上传机票文件 ({batchUploadCompletedCount}/{batchUploadFiles.length})
            </div>
            <Progress
              percent={batchUploadProgress}
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              style={{ maxWidth: 400, margin: '0 auto' }}
            />
            {batchUploadCurrentFile && (
              <div style={{ marginTop: 16 }}>
                <div style={{ color: '#666', marginBottom: 4 }}>当前文件:</div>
                <div style={{
                  color: '#1890ff',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  background: '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: 4,
                  maxWidth: 500,
                  margin: '0 auto',
                  wordBreak: 'break-all'
                }}>
                  {batchUploadCurrentFile}
                </div>
              </div>
            )}
            <div style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
              请勿关闭此窗口，正在逐个上传并匹配游客...
            </div>
          </div>
        ) : (
          // 上传表单
          <div>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">
                选择出发日期和订票系统，上传机票PDF文件，系统将自动识别PDF内容并通过护照号匹配游客。
              </Typography.Text>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>1. 选择目的地</div>
              <Select
                placeholder="选择目的地"
                style={{ width: '100%' }}
                value={batchUploadDestination}
                onChange={(destId) => {
                  setBatchUploadDestination(destId);
                  // 当目的地改变时，检查当前选择的出发日期是否属于新目的地
                  if (destId && batchUploadDate) {
                    const availableDates = tours
                      .filter(t => t.destinationId === destId)
                      .map(t => t.departureDate ? moment(t.departureDate).format('YYYY-MM-DD') : null)
                      .filter(Boolean);
                    if (!availableDates.includes(batchUploadDate)) {
                      setBatchUploadDate(null);
                    }
                  }
                }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {destinations.map(dest => (
                  <Option key={dest.id} value={dest.id}>{dest.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>2. 选择出发日期</div>
              <Select
                placeholder="选择出发日期"
                style={{ width: '100%' }}
                value={batchUploadDate}
                onChange={setBatchUploadDate}
                showSearch
              >
                {[...new Set(
                  tours
                    .filter(t => !batchUploadDestination || t.destinationId === batchUploadDestination)
                    .map(t => t.departureDate ? moment(t.departureDate).format('YYYY-MM-DD') : null)
                    .filter(Boolean)
                )]
                  .sort()
                  .map(date => (
                    <Option key={date} value={date}>{date}</Option>
                  ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>3. 选择订票系统</div>
              <Select
                placeholder="选择订票系统"
                style={{ width: '100%' }}
                value={batchUploadAirline}
                onChange={setBatchUploadAirline}
                showSearch
                optionFilterProp="children"
              >
                {batchUploadAirlines.map(a => (
                  <Option key={a.id} value={a.id}>{a.name} ({a.code})</Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>4. 上传机票文件</div>
              <Upload.Dragger
                accept=".pdf"
                multiple
                beforeUpload={() => false}
                onChange={handleBatchFileChange}
                fileList={batchUploadFiles.map((file, index) => ({
                  uid: index,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  type: file.type
                }))}
                disabled={batchUploading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽PDF文件到此区域</p>
                <p className="ant-upload-hint">
                  支持多文件上传，系统将自动识别PDF内容匹配游客
                </p>
              </Upload.Dragger>
            </div>

            {batchUploadFiles.length > 0 && (
              <div style={{ color: '#1890ff' }}>
                已选择 {batchUploadFiles.length} 个文件
              </div>
            )}

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ color: '#999', fontSize: 12 }}>
              <div style={{ marginBottom: 4 }}>说明：</div>
              <div>• 系统会自动从PDF内容中提取乘客姓名、护照号和票号</div>
              <div>• 通过护照号自动匹配对应游客，无需特定文件命名格式</div>
            </div>
          </div>
        )}
      </Modal>

      {/* 批量上传酒店预订单弹窗 */}
      <Modal
        title="批量上传酒店预订单"
        open={batchHotelModalVisible}
        onCancel={() => {
          setBatchHotelModalVisible(false);
          resetBatchHotelUpload();
        }}
        width={700}
        footer={
          batchHotelResult ? [
            <Button key="close" onClick={() => {
              setBatchHotelModalVisible(false);
              resetBatchHotelUpload();
            }}>
              关闭
            </Button>,
            <Button key="upload-more" type="primary" onClick={resetBatchHotelUpload}>
              继续上传
            </Button>
          ] : [
            <Button key="cancel" onClick={() => {
              setBatchHotelModalVisible(false);
              resetBatchHotelUpload();
            }}>
              取消
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={batchHotelUploading}
              onClick={handleBatchHotelUpload}
              disabled={!batchHotelDate || batchHotelFiles.length === 0}
            >
              开始上传
            </Button>
          ]
        }
      >
        {batchHotelResult ? (
          // 上传结果展示
          <div>
            <div style={{ marginBottom: 16 }}>
              <Space size="large">
                <Tag color="success" style={{ padding: '4px 12px', fontSize: 14 }}>
                  <CheckCircleOutlined /> 成功: {batchHotelResult.success?.length || 0}
                </Tag>
                <Tag color="warning" style={{ padding: '4px 12px', fontSize: 14 }}>
                  <WarningOutlined /> 未匹配: {batchHotelResult.notMatched?.length || 0}
                </Tag>
                <Tag color="error" style={{ padding: '4px 12px', fontSize: 14 }}>
                  <ExclamationCircleOutlined /> 失败: {batchHotelResult.errors?.length || 0}
                </Tag>
              </Space>
            </div>

            {batchHotelResult.success?.length > 0 && (
              <Card size="small" title="上传成功" style={{ marginBottom: 12 }}>
                <List
                  size="small"
                  dataSource={batchHotelResult.success}
                  renderItem={(item) => (
                    <List.Item>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      <span>{item.fileName}</span>
                      <span style={{ marginLeft: 8, color: '#666' }}>
                        {item.travelerName} → {item.touristName}
                      </span>
                    </List.Item>
                  )}
                  style={{ maxHeight: 200, overflow: 'auto' }}
                />
              </Card>
            )}

            {batchHotelResult.notMatched?.length > 0 && (
              <Card size="small" title="未匹配游客" style={{ marginBottom: 12 }}>
                <List
                  size="small"
                  dataSource={batchHotelResult.notMatched}
                  renderItem={(item) => (
                    <List.Item>
                      <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                      <span>{item.fileName}</span>
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        {item.travelers && `(识别到: ${item.travelers.join(', ')})`}
                        {item.reason}
                      </span>
                    </List.Item>
                  )}
                  style={{ maxHeight: 150, overflow: 'auto' }}
                />
              </Card>
            )}

            {batchHotelResult.errors?.length > 0 && (
              <Card size="small" title="上传失败" style={{ marginBottom: 12 }}>
                <List
                  size="small"
                  dataSource={batchHotelResult.errors}
                  renderItem={(item) => (
                    <List.Item>
                      <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      <span>{item.fileName}</span>
                      <span style={{ marginLeft: 8, color: '#ff4d4f' }}>{item.error}</span>
                    </List.Item>
                  )}
                  style={{ maxHeight: 100, overflow: 'auto' }}
                />
              </Card>
            )}
          </div>
        ) : batchHotelUploading ? (
          // 上传中显示进度条
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ marginBottom: 16 }}>
              <HomeOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </div>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
              正在上传酒店预订单 ({batchHotelCompletedCount}/{batchHotelFiles.length})
            </div>
            <Progress
              percent={batchHotelProgress}
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              style={{ maxWidth: 400, margin: '0 auto' }}
            />
            {batchHotelCurrentFile && (
              <div style={{ marginTop: 16 }}>
                <div style={{ color: '#666', marginBottom: 4 }}>当前文件:</div>
                <div style={{
                  color: '#1890ff',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  background: '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: 4,
                  maxWidth: 500,
                  margin: '0 auto',
                  wordBreak: 'break-all'
                }}>
                  {batchHotelCurrentFile}
                </div>
              </div>
            )}
            <div style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
              请勿关闭此窗口，正在识别PDF并匹配游客...
            </div>
          </div>
        ) : (
          // 上传表单
          <div>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary">
                选择出发日期，然后上传酒店预订单PDF文件，系统将自动识别PDF中的旅客姓名并匹配游客。
              </Typography.Text>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>1. 选择出发日期</div>
              <Select
                placeholder="选择出发日期"
                style={{ width: '100%' }}
                value={batchHotelDate}
                onChange={setBatchHotelDate}
                showSearch
              >
                {[...new Set(tours.map(t => t.departureDate ? moment(t.departureDate).format('YYYY-MM-DD') : null).filter(Boolean))]
                  .sort()
                  .map(date => (
                    <Option key={date} value={date}>{date}</Option>
                  ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>2. 上传酒店预订单文件</div>
              <Upload.Dragger
                accept=".pdf"
                multiple
                beforeUpload={() => false}
                onChange={handleBatchHotelFileChange}
                fileList={batchHotelFiles.map((file, index) => ({
                  uid: index,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  type: file.type
                }))}
                disabled={batchHotelUploading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽PDF文件到此区域</p>
                <p className="ant-upload-hint">
                  支持多文件上传，系统将自动识别PDF中的Traveler信息
                </p>
              </Upload.Dragger>
            </div>

            {batchHotelFiles.length > 0 && (
              <div style={{ color: '#1890ff' }}>
                已选择 {batchHotelFiles.length} 个文件
              </div>
            )}

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ color: '#999', fontSize: 12 }}>
              <div style={{ marginBottom: 4 }}>说明：</div>
              <div>• 系统会自动识别PDF中的 Traveler 1, Traveler 2 等旅客信息</div>
              <div>• 识别到的姓名将与游客列表进行匹配</div>
              <div>• 同一个预订单会自动分配给所有匹配的游客</div>
            </div>
          </div>
        )}
      </Modal>

      {/* 护照照片查看弹窗 */}
      <Modal
        title={`护照照片 - ${passportModalRecord?.passportName || passportModalRecord?.touristName || ''}`}
        open={passportModalVisible}
        onCancel={() => {
          setPassportModalVisible(false);
          setPassportModalRecord(null);
        }}
        footer={null}
        width={700}
      >
        {passportModalRecord && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Image
                src={passportModalRecord.passportPhoto.startsWith('http')
                  ? passportModalRecord.passportPhoto
                  : `${API_BASE.replace('/api', '')}${passportModalRecord.passportPhoto}`}
                alt="护照照片"
                style={{ maxWidth: '100%', maxHeight: 500 }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMoq6muYv8AEVHGAROIGB4s+MACDkNh0EYcxAIGBnU"
              />
            </div>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                <div><strong>姓名：</strong>{passportModalRecord.passportName || '-'}</div>
                <div><strong>护照号码：</strong>{passportModalRecord.passportNumber || '-'}</div>
                <div><strong>国籍：</strong>{passportModalRecord.nationality || '-'}</div>
                <div><strong>性别：</strong>{passportModalRecord.gender === 'M' ? '男' : passportModalRecord.gender === 'F' ? '女' : '-'}</div>
                <div><strong>出生日期：</strong>{passportModalRecord.passportBirthDate ? moment(passportModalRecord.passportBirthDate).format('YYYY-MM-DD') : '-'}</div>
                <div><strong>签发日期：</strong>{passportModalRecord.passportIssueDate ? moment(passportModalRecord.passportIssueDate).format('YYYY-MM-DD') : '-'}</div>
                <div><strong>有效期至：</strong>{passportModalRecord.passportExpiryDate ? moment(passportModalRecord.passportExpiryDate).format('YYYY-MM-DD') : '-'}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 身份证 / 台胞证查看弹窗（中国团附加证件） */}
      <Modal
        title={`${getChinaDocLabel(chinaDocModalRecord) || '证件'} - ${chinaDocModalRecord?.passportName || chinaDocModalRecord?.touristName || ''}`}
        open={chinaDocModalVisible}
        onCancel={() => {
          setChinaDocModalVisible(false);
          setChinaDocModalRecord(null);
        }}
        footer={null}
        width={700}
      >
        {chinaDocModalRecord?.chinaDocPhoto && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Image
                src={chinaDocModalRecord.chinaDocPhoto.startsWith('http')
                  ? chinaDocModalRecord.chinaDocPhoto
                  : `${API_BASE.replace('/api', '')}${chinaDocModalRecord.chinaDocPhoto}`}
                alt={getChinaDocLabel(chinaDocModalRecord) || '证件'}
                style={{ maxWidth: '100%', maxHeight: 500 }}
              />
            </div>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                <div><strong>证件类型：</strong>{getChinaDocLabel(chinaDocModalRecord) || '-'}</div>
                <div><strong>证件姓名：</strong>{chinaDocModalRecord.chinaDocName || '-'}</div>
                <div><strong>证件号码：</strong>{chinaDocModalRecord.chinaDocNumber || '-'}</div>
                <div><strong>有效期至：</strong>{chinaDocModalRecord.chinaDocExpiryDate ? moment(chinaDocModalRecord.chinaDocExpiryDate).format('YYYY-MM-DD') : '-'}</div>
                <div><strong>上传时间：</strong>{chinaDocModalRecord.chinaDocUploadedAt ? moment(chinaDocModalRecord.chinaDocUploadedAt).format('YYYY-MM-DD HH:mm') : '-'}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AdminTourists;
